import { Injectable } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import * as CookieConsentActions from '@planone/shared/feature-cookie-consent';
import { GoogleAnalyticsService } from '@planone/shared/feature-tracking-google-analytics';
import { delay, filter, tap } from 'rxjs';

import * as SnowplowActions from './snowplow.actions';

import { SnowplowService } from '../service/snowplow.service';

@Injectable()
export class SnowplowEffects {
  initialize$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SnowplowActions.initialize),
        tap((action) => {
          this.spService.initialize(action);
        })
      ),
    { dispatch: false }
  );

  cookieConsent$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CookieConsentActions.initialize, CookieConsentActions.changed),
        tap((action) => this.spService.anonymize(!action.status))
      ),
    { dispatch: false }
  );

  processEventQueue$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CookieConsentActions.initialize, CookieConsentActions.changed),
        filter((action) => action.status !== undefined),
        delay(0),
        tap(() => this.spService.processQueue())
      ),
    { dispatch: false }
  );

  cookieConsentReset$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CookieConsentActions.reset),
        tap(() => {
          this.spService.anonymize(true);
          this.gaService.push({ event: 'pageview_consent' });
        })
      ),
    { dispatch: false }
  );

  anonymize$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SnowplowActions.anonymize),
        tap((action) => {
          this.spService.anonymize(action.anonymize);
        })
      ),
    { dispatch: false }
  );

  pageView$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SnowplowActions.pageView),
        filter((action) => !!action.enriched),
        tap((action) => {
          let pageTitle = action['title'] ?? this.title.getTitle();

          if (action.title_context) {
            const pageTitleSections = pageTitle.split(' | ');

            pageTitleSections[pageTitleSections.length - 1] =
              action.title_context + ' | ' + pageTitleSections[pageTitleSections.length - 1];
            pageTitle = pageTitleSections.join(' | ');
          }

          const modifiableAction = { ...action };

          if (Object.keys(modifiableAction).includes('title_context')) {
            delete modifiableAction.title_context;
          }

          this.spService.pageView({
            ...modifiableAction,
            title: pageTitle,
          });
        })
      ),
    { dispatch: false }
  );

  selfDescribingEvent$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SnowplowActions.selfDescribingEvent),
        filter((action) => !!action.enriched),
        tap((action) => this.spService.selfDescribingEvent(action))
      ),
    { dispatch: false }
  );

  linkClick$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SnowplowActions.linkClick),
        filter((action) => !!action.enriched),
        tap((action) => {
          this.spService.linkClick(action);
        })
      ),
    { dispatch: false }
  );

  enableYoutubeTracking$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SnowplowActions.enableYoutubeTracking),
        tap((action) => {
          this.spService.enableYoutubeTracking(action);
        })
      ),
    { dispatch: false }
  );

  setUserId$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SnowplowActions.setUserId),
        tap((action) => {
          this.spService.setUserId(action.tokenSub);
        })
      ),
    { dispatch: false }
  );

  constructor(
    private readonly actions$: Actions,
    private spService: SnowplowService,
    private gaService: GoogleAnalyticsService,
    private title: Title
  ) {}
}
